// Correlates a fetch `Response` with the W3C trace-id the frontend generated
// for that request. The OTel fetch instrumentation injects this id into the
// outgoing `traceparent` header, and the backend continues (and logs) the same
// trace — so surfacing it on a failed call lets support match the error to its
// server-side trace in Grafana Tempo.
//
// Keyed by the Response object so concurrent requests never clobber each other,
// and so entries are garbage-collected together with the Response.
const responseTraceIds = new WeakMap<Response, string>()

export function rememberTraceId(response: Response, traceId: string): void {
  if (traceId) {
    responseTraceIds.set(response, traceId)
  }
}

export function getTraceId(response: unknown): string | undefined {
  if (response instanceof Response) {
    return responseTraceIds.get(response)
  }

  return undefined
}

// Resolve a trace-id from an arbitrary thrown error. Covers ofetch's FetchError
// (which carries the failing Response on `.response`) and our own error classes
// that expose a `traceId` directly (SseStreamError, ResourceCreationApiError).
export function getTraceIdFromError(error: unknown): string | undefined {
  if (error && typeof error === 'object') {
    const candidate = error as { traceId?: unknown; response?: unknown }

    if (typeof candidate.traceId === 'string' && candidate.traceId) {
      return candidate.traceId
    }

    const fromResponse = getTraceId(candidate.response)
    if (fromResponse) {
      return fromResponse
    }
  }

  return undefined
}

// `Trace ID: <id>` line for surfacing in an error message, or undefined when no
// trace-id is available so callers can omit it.
export function formatTraceIdLine(
  traceId?: string,
  label = 'Trace ID'
): string | undefined {
  return traceId ? `${label}: ${traceId}` : undefined
}

// Append the trace-id to an existing message on its own line, if present.
export function appendTraceId(
  message: string,
  traceId?: string,
  label = 'Trace ID'
): string {
  const line = formatTraceIdLine(traceId, label)
  return line ? `${message}\n${line}` : message
}

// Nuxt UI toast `actions` that copy the trace-id to the clipboard — toasts
// auto-dismiss and their text can't be selected, so a copy button is the only
// reliable way for the user to grab the id for a bug report. Returns undefined
// when there's no trace-id, so callers can spread it without a guard.
export function traceIdToastActions(traceId?: string, copyLabel = 'Copy') {
  if (!traceId) {
    return
  }

  return [
    {
      label: copyLabel,
      icon: 'i-lucide-copy',
      color: 'neutral' as const,
      variant: 'outline' as const,
      onClick: () => {
        navigator.clipboard?.writeText(traceId).catch((error: unknown) => {
          // Clipboard access can be denied (insecure context, permissions).
          // Warn rather than fail silently so it's diagnosable.
          console.warn('Failed to copy trace-id to clipboard:', error)
        })
      },
    },
  ]
}
